import type { QueryClient } from "@tanstack/vue-query";
import { authenticationService } from "../services/authentication.service";

/** Les projections d'un même domaine ont des clés différentes. Invalider
 * seulement la liste de gestion laisse les vues personnelles/publiques fraîches. */
const invalidateMeetingEntities = (
  client: QueryClient,
  meetingId: string | null | undefined,
  entities: readonly string[],
) =>
  Promise.all(
    entities.map((entity) =>
      client.invalidateQueries({
        queryKey: ["meetings", meetingId, entity],
      }),
    ),
  );

export const invalidateDailyTaskData = (
  client: QueryClient,
  meetingId: string | null | undefined,
) =>
  invalidateMeetingEntities(client, meetingId, [
    "daily-tasks",
    "team-daily-tasks",
    "my-daily-tasks",
  ]);

export const invalidateEventData = (
  client: QueryClient,
  meetingId: string | null | undefined,
) =>
  invalidateMeetingEntities(client, meetingId, [
    "activities",
    "concret-events",
    "generic-events",
    "public-events",
    "typical-day-events",
    "modular-events",
    "public-modular-events",
    "my-event",
    "my-next-resp-event",
    "my-next-in-charge-event",
    "events",
  ]);

/** La liste « Mes stages » est la clé exacte [meetings] ; son invalidation
 * ne doit pas rafraîchir tous les sous-arbres de tous les stages. */
export const invalidateMeetingLists = (client: QueryClient) =>
  Promise.all([
    client.invalidateQueries({ queryKey: ["meetings"], exact: true }),
    ...["all-meetings", "scope-meetings", "ongoing-meetings"].map((key) =>
      client.invalidateQueries({ queryKey: [key] }),
    ),
  ]);

export const invalidateMeetingData = (
  client: QueryClient,
  meetingId: string | null | undefined,
) =>
  Promise.all([
    client.invalidateQueries({ queryKey: ["meetings", meetingId] }),
    client.invalidateQueries({ queryKey: ["public-meetings", meetingId] }),
    invalidateMeetingLists(client),
  ]);

export const invalidateMembershipData = (
  client: QueryClient,
  meetingId: string,
) => {
  // getUser() garde aussi une copie locale du profil pendant douze heures.
  authenticationService.invalidateUserCache();
  return Promise.all([
    client.invalidateQueries({ queryKey: ["me"] }),
    client.invalidateQueries({ queryKey: ["access_rights"] }),
    invalidateMeetingData(client, meetingId),
  ]);
};
